import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Lock, Download, CreditCard, CheckCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
interface PaidContent {
  id: string;
  title: string;
  description: string;
  price_cents: number;
  file_name: string;
  file_type: string;
  keywords: string[];
  creator_id: string;
}

interface ContentPaywallProps {
  content: PaidContent;
  creatorName?: string;
  onPurchaseComplete?: (content: PaidContent) => void;
  className?: string;
}

const ContentPaywall = ({ 
  content, 
  creatorName = "Creator", 
  onPurchaseComplete,
  className 
}: ContentPaywallProps) => {
  const [loading, setLoading] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [purchased, setPurchased] = useState(false);
  const { toast } = useToast();

  const formatPrice = (cents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(cents / 100);
  };

  const handlePurchase = async () => {
    setLoading(true);
    
    try {
      // Create checkout session using existing create-posit-checkout function
      const { data, error } = await supabase.functions.invoke('create-posit-checkout', {
        body: { 
          posit_id: content.id,
          success_url: `${window.location.origin}/dashboard`,
          cancel_url: window.location.href
        }
      });

      if (error) throw error;

      // Redirect to Stripe Checkout
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }

    } catch (error: any) {
      
      if (error.message?.includes('already purchased')) {
        toast({
          title: "Already purchased",
          description: "You already own this content",
          variant: "destructive"
        });
      } else if (error.message?.includes('Cannot purchase your own')) {
        toast({
          title: "Cannot purchase",
          description: "You cannot purchase your own content",
          variant: "destructive"
        });
      } else {
        toast({
          title: "Purchase failed",
          description: error.message || "Failed to process purchase",
          variant: "destructive"
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const downloadContent = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('get-content-access', {
        body: { content_id: content.id }
      });

      if (error) throw error;

      if (data.signed_url) {
        const link = document.createElement('a');
        link.href = data.signed_url;
        link.download = content.file_name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        toast({
          title: "Download started",
          description: `Downloading ${content.file_name}`
        });
      }
    } catch (error: any) {
      toast({
        title: "Download failed",
        description: error.message || "Could not download the file",
        variant: "destructive"
      });
    }
  };

  if (purchased) {
    return (
      <Card className={`border-green-200 bg-green-50 ${className}`}>
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <CheckCircle className="h-8 w-8 text-green-600" />
            <div className="flex-1">
              <h4 className="font-semibold text-green-900">{content.title}</h4>
              <p className="text-sm text-green-700">Purchase complete! You can now download this content.</p>
            </div>
            <Button onClick={downloadContent} size="sm" className="bg-green-600 hover:bg-green-700">
              <Download className="h-4 w-4 mr-2" />
              Download
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className={`border-amber-200 bg-amber-50 ${className}`}>
        <CardHeader className="pb-3">
          <div className="flex items-start gap-3">
            <Lock className="h-6 w-6 text-amber-600 mt-1" />
            <div className="flex-1">
              <CardTitle className="text-lg text-amber-900">{content.title}</CardTitle>
              <CardDescription className="text-amber-700">
                Premium content by {creatorName}
              </CardDescription>
            </div>
            <Badge variant="secondary" className="bg-amber-100 text-amber-800">
              {formatPrice(content.price_cents)}
            </Badge>
          </div>
        </CardHeader>
        
        <CardContent className="pt-0 space-y-4">
          {content.description && (
            <p className="text-sm text-amber-800">{content.description}</p>
          )}
          
          <div className="flex items-center gap-2 text-xs text-amber-700">
            <span>File: {content.file_name}</span>
            <span>•</span>
            <span>Type: {content.file_type}</span>
          </div>

          {content.keywords && content.keywords.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {content.keywords.slice(0, 3).map((keyword) => (
                <Badge key={keyword} variant="outline" className="text-xs border-amber-300 text-amber-700">
                  {keyword}
                </Badge>
              ))}
              {content.keywords.length > 3 && (
                <Badge variant="outline" className="text-xs border-amber-300 text-amber-700">
                  +{content.keywords.length - 3} more
                </Badge>
              )}
            </div>
          )}

          <div className="flex gap-2">
            <Button
              onClick={() => setShowDialog(true)}
              size="sm"
              className="flex-1 bg-amber-600 hover:bg-amber-700"
            >
              <CreditCard className="h-4 w-4 mr-2" />
              Purchase for {formatPrice(content.price_cents)}
            </Button>
            <Button
              onClick={() => setShowDialog(true)}
              variant="outline"
              size="sm"
              className="border-amber-300 text-amber-700 hover:bg-amber-100"
            >
              Preview
            </Button>
          </div>
        </CardContent>
      </Card>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Purchase Content</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="border rounded-lg p-4 bg-muted/50">
              <h4 className="font-semibold">{content.title}</h4>
              <p className="text-sm text-muted-foreground mt-1">
                {content.description || "Premium content file"}
              </p>
              <div className="flex items-center justify-between mt-3">
                <span className="text-sm text-muted-foreground">
                  File: {content.file_name}
                </span>
                <Badge variant="secondary">
                  {formatPrice(content.price_cents)}
                </Badge>
              </div>
            </div>

            <div className="text-sm space-y-2 text-muted-foreground">
              <p>• Instant download after purchase</p>
              <p>• Lifetime access to this content</p>
              <p>• Secure payment through Stripe</p>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={() => setShowDialog(false)}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handlePurchase}
                disabled={loading}
                className="flex-1"
              >
                {loading ? "Processing..." : `Purchase ${formatPrice(content.price_cents)}`}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ContentPaywall; 